import { defineStore } from "pinia";
import {
  DataBoundaryGeometry,
  useApi,
  useDataSource,
  useGammaData,
} from "@/services";
import { useAxios } from "@/config";
import { DatasetRecord, GDT } from "@/types";
import { AxiosResponse } from "axios";

interface GroupsDeleteRequest {
  filter: GDT.DataFilter[];
}
interface ColumnInsert {
  column: {
    columnName: string;
  };
  value: any;
}
interface GroupsAddRequest {
  columnInserts: ColumnInsert[];
}

interface GroupedRecordsState {
  groups: string[];
  selectedGroup: string;
  selectedRecords: DatasetRecord[];
  tableDefinition: null | GDT.TableDefinition;
  data: null | GDT.Data;
  retries: number;
  refreshAttempts: number;
  screenGroups: string[];
}

export const useGroupedRecords = defineStore("groupedRecords", {
  state: (): GroupedRecordsState => ({
    groups: [],
    selectedGroup: "",
    selectedRecords: [],
    tableDefinition: null,
    data: null,
    retries: 5,
    refreshAttempts: 0,
    screenGroups: [],
  }),
  getters: {
    groupsSet(): Set<string> {
      return new Set(this.groups);
    },
  },
  actions: {
    async fetchGroupsMeta(): Promise<void> {
      return useAxios()
        .$http.get<string, AxiosResponse<GDT.TableDefinition>>(
          useApi().groupsMeta
        )
        .then(({ data }) => {
          this.tableDefinition = data;
        });
    },
    async fetchGroupsData(): Promise<void> {
      return useAxios()
        .$http.get<string, AxiosResponse<GDT.Data>>(useApi().groupsData)
        .then(({ data }) => {
          this.data = data;
        });
    },
    async fetchGroupsMetaData(): Promise<void> {
      return useAxios()
        .$http.get<
          string,
          AxiosResponse<{ meta: GDT.TableDefinition; data: GDT.Data }>
        >(useApi().groupsMetaData)
        .then(({ data }) => {
          this.tableDefinition = data.meta;
          this.data = data.data;
        });
    },
    groupsAdd(payload: GroupsAddRequest): Promise<void> {
      return useAxios().$http.put(useApi().groupsAdd, payload);
    },
    async groupsDelete(payload: GroupsDeleteRequest): Promise<void> {
      return useAxios()
        .$http.delete(useApi().groupsDelete, { params: payload })
        .then((r) => console.log("groupsDelete", r.data));
    },
    async fetchGroupsList(): Promise<void> {
      return useAxios()
        .$http.get<string, AxiosResponse<string[]>>(useApi().groupsGetGroupList)
        .then(({ data }) => {
          this.groups = [...new Set(data)];
        });
    },
    async fetchScreenGroupList(boundary: DataBoundaryGeometry): Promise<void> {
      const dataSource = useDataSource("group_boundaries")();
      if (!dataSource.isActive) {
        dataSource.isActive = true;
      }

      const request = {
        catalogUri: dataSource.catalogUri,
        displayColumns: [
          {
            aggregateFunction: "distinct",
            name: "GROUP_NUMBER",
            label: "GROUP_NUMBER",
            orderInSelectList: 1,
          },
        ],
        filter: [
          {
            functionName: "FieldIntersectsGeometryFromGeoJson",
            arguments: [
              {
                name: "field",
                value: "geom",
              },
              {
                name: "geojson",
                value: boundary,
              },
              {
                name: "srid",
                value: "4326",
              },
            ],
            conditionOperator: 0,
          },
        ],
        orderBys: [],
        groupBys: [],
      } as GDT.GetDataRequest;

      const result = await useGammaData().fetchData(request);
      this.screenGroups = result.data?.data?.rows?.map((x: any) => "" + x[0]);
    },
    async groupsGetNewGroupId(): Promise<string> {
      const { data } = await useAxios().$http.get<
        string,
        AxiosResponse<number>
      >(useApi().groupsGetNewGroupId);
      this.selectedGroup = data.toString();
      return this.selectedGroup;
    },
    async groupHasPolicies(
      group: string,
      catalogUri: string
    ): Promise<boolean> {
      return useAxios()
        .$http.post<string, AxiosResponse<any>>(
          `${catalogUri}/${GDT.Action.Data}`,
          {
            catalogUri,
            displayColumns: [
              {
                aggregateFunction: "count",
                name: "POLICY_ID",
                label: "POLICY_ID",
                orderInSelectList: 1,
              },
            ],
            filter: [
              {
                fieldName: "GROUP_NUMBER",
                filterOperator: 0,
                value: group,
              },
            ],
          }
        )
        .then(({ data }) => {
          return data.data.rows[0] > 0;
        });
    },
    async delete(groupNumber: number): Promise<void> {
      await useAxios().$http.delete(useApi().groupsDelete, {
        data: {
          filter: [
            {
              fieldName: "group_number",
              filterOperator: 0,
              conditionOperator: 0,
              value: groupNumber,
            },
          ],
        },
      });
    },
  },
});
